import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  GovernanceCheckCommitRequest,
  GovernanceCheckResponse,
} from '$/requests/GovernanceCheck.ts';
import { BackendErrorResponse } from '@/types/response/BackendErrorResponse';

const useGovernanceCheck = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: GovernanceCheckCommitRequest) => submitGovernanceCheck(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
    },
  });
};

export default useGovernanceCheck;

async function submitGovernanceCheck(
  body: GovernanceCheckCommitRequest,
): Promise<GovernanceCheckResponse> {
  const { data, error } = await supabaseClient.functions.invoke('governance-check', { body });

  if (error) {
    if (error instanceof FunctionsHttpError) {
      const errorResponse: BackendErrorResponse = await error.context.json();
      throw new Error(errorResponse.errors?.join(', ') ?? 'Governance check failed');
    }
    throw new Error(error.message || 'Governance check failed');
  }

  const payload = data as { data?: GovernanceCheckResponse; errors?: string[] };
  if (payload?.errors?.length) {
    throw new Error(payload.errors.join(', '));
  }

  if (!payload?.data) {
    throw new Error('Governance check failed');
  }

  return payload.data;
}
